import { useState } from "react";
import { api } from "../lib/api";

export type TrafficFormValues = {
	displayName: string;
	vertical: string;
	subverticals: string;
	geos: string;
	sources: string;
	monthlyVolume: string;
	avgConversionRate: string;
	fraudScore: string;
};

export const EMPTY_TRAFFIC: TrafficFormValues = {
	displayName: "",
	vertical: "casino",
	subverticals: "slots",
	geos: "DE,AT",
	sources: "seo,ppc",
	monthlyVolume: "5000",
	avgConversionRate: "0.05",
	fraudScore: "0.05",
};

function csv(s: string): string[] {
	return s
		.split(",")
		.map((x) => x.trim())
		.filter(Boolean);
}

export function trafficPayload(v: TrafficFormValues) {
	return {
		displayName: v.displayName,
		vertical: v.vertical,
		subverticals: csv(v.subverticals),
		geos: csv(v.geos).map((g) => g.toUpperCase()),
		sources: csv(v.sources),
		monthlyVolume: Number.parseInt(v.monthlyVolume, 10) || 0,
		avgConversionRate: Number.parseFloat(v.avgConversionRate),
		fraudScore: Number.parseFloat(v.fraudScore),
	};
}

export function TrafficForm(props: {
	initial: TrafficFormValues;
	submitLabel: string;
	onSubmit: (v: TrafficFormValues) => Promise<void>;
	onCancel: () => void;
	error?: string | null;
}) {
	const [v, setV] = useState<TrafficFormValues>(props.initial);
	const [submitting, setSubmitting] = useState(false);
	const [localError, setLocalError] = useState<string | null>(null);
	const err = localError ?? props.error;

	function field<K extends keyof TrafficFormValues>(k: K) {
		return (val: TrafficFormValues[K]) => setV((prev) => ({ ...prev, [k]: val }));
	}

	async function submit() {
		setLocalError(null);
		setSubmitting(true);
		try {
			await props.onSubmit(v);
		} catch (e) {
			setLocalError((e as Error).message);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="max-w-xl space-y-4">
			<div className="grid gap-3">
				<TextInput label="Display name" value={v.displayName} onChange={field("displayName")} />
				<SelectInput
					label="Vertical"
					value={v.vertical}
					options={["casino", "sportsbook", "poker", "fintech", "crypto"]}
					onChange={field("vertical")}
				/>
				<TextInput
					label="Subverticals (comma)"
					value={v.subverticals}
					onChange={field("subverticals")}
				/>
				<TextInput label="Geos (ISO-2 comma)" value={v.geos} onChange={field("geos")} />
				<TextInput label="Sources (comma)" value={v.sources} onChange={field("sources")} />
				<TextInput
					label="Monthly volume"
					value={v.monthlyVolume}
					onChange={field("monthlyVolume")}
				/>
				<TextInput
					label="Avg conversion rate (0..1)"
					value={v.avgConversionRate}
					onChange={field("avgConversionRate")}
				/>
				<TextInput label="Fraud score (0..1)" value={v.fraudScore} onChange={field("fraudScore")} />
			</div>
			{err && <div className="text-sm text-red-700">{err}</div>}
			<div className="flex gap-2">
				<button
					type="button"
					onClick={submit}
					disabled={submitting || !v.displayName}
					className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
				>
					{props.submitLabel}
				</button>
				<button
					type="button"
					onClick={props.onCancel}
					className="rounded bg-neutral-200 px-3 py-1 text-sm hover:bg-neutral-300"
				>
					Cancel
				</button>
			</div>
		</div>
	);
}

function TextInput(props: { label: string; value: string; onChange: (v: string) => void }) {
	return (
		<label className="text-sm">
			{props.label}
			<input
				className="block w-full rounded border px-2 py-1"
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
			/>
		</label>
	);
}

function SelectInput(props: {
	label: string;
	value: string;
	options: string[];
	onChange: (v: string) => void;
}) {
	return (
		<label className="text-sm">
			{props.label}
			<select
				className="block w-full rounded border px-2 py-1"
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
			>
				{props.options.map((o) => (
					<option key={o} value={o}>
						{o}
					</option>
				))}
			</select>
		</label>
	);
}

export async function createTraffic(v: TrafficFormValues): Promise<{ id: string }> {
	return api.post<{ id: string }>("/traffic", trafficPayload(v));
}

export async function updateTraffic(id: string, v: TrafficFormValues): Promise<void> {
	await api.patch(`/traffic/${id}`, trafficPayload(v));
}
